/*
 * The channel a Field uses to reach the control inside it.
 *
 * Kept in its own module so that a control can consume it without importing
 * Field, which would make every input in the system depend on the wrapper it is
 * usually, but not always, used inside.
 */

import { createContext, useContext } from 'react'

export interface FieldControl {
  /** The id the control must adopt, so the Field's `<label for>` reaches it. */
  id: string
  /** The hint and error ids, already joined, or undefined when there are none. */
  describedBy: string | undefined
  /** True when the Field is showing an error. Becomes `aria-invalid`. */
  invalid: boolean
  /**
   * True when the Field is marked required. Controls turn this into
   * `aria-required` rather than the native `required` attribute: the native one
   * additionally hands validation to the browser, which then blocks submission
   * with a bubble in the browser's language, positioned by the browser, that
   * disappears on the next keystroke. The console validates on submit and shows
   * the reason in the Field, where it stays put and can be read twice.
   */
  required: boolean
}

export const FieldContext = createContext<FieldControl | null>(null)

/**
 * The wiring a control should adopt, or null when it is standing on its own.
 *
 * Every control in this layer lets an explicit prop win over what it reads
 * here. A caller who passes their own `id` or `aria-describedby` has a reason,
 * usually that the control is described by something outside the Field, and
 * silently overwriting it would be the harder failure to find.
 */
export function useFieldControl(): FieldControl | null {
  return useContext(FieldContext)
}
